import { nextTick } from 'vue';
import { createI18n, type I18n, type Locale } from 'vue-i18n';
import type { Router } from 'vue-router';

export const SUPPORTED_LOCALES = ['en', 'de', 'it', 'fr'];

type ProjectI18n = I18n<unknown, unknown, unknown, true>;

const defaultOptions = { locale: 'de', fallbackLocale: 'de', messages: {}, legacy: false, };

export function changeLocale(router: Router, locale: Locale) {
  const targetRoute = router.currentRoute.value;
  if (targetRoute.params.locale !== locale) {
    router.push({
      name: targetRoute.name ?? undefined,
      params: {
        ...targetRoute.params,
        locale,
      },
    });
  }
}

export function setI18nLanguage(i18n: ProjectI18n, locale: Locale) {
  i18n.global.locale.value = locale;
  /**
   * NOTE:
   * If you need to specify the language setting for headers, such as the `fetch` API, set it here.
   * The following is an example for axios.
   *
   * axios.defaults.headers.common['Accept-Language'] = locale
   */
  document.querySelector('html')?.setAttribute('lang', locale);
}

export async function loadLocaleMessages(i18n: ProjectI18n, locale: Locale) {
  // load locale messages with dynamic import
  try {
    const messages = await import(`./locales/${locale}.json`);

    // set locale and locale message
    i18n.global.setLocaleMessage(locale, messages.default);

    console.log(`Loaded ${locale} locale with ${Object.keys(messages.default).length} translations`);
  } catch (error) {
    console.error(`Failed to load locale ${locale}:`, error);
    // Fallback to empty object to prevent errors
    i18n.global.setLocaleMessage(locale, {});
  }

  return nextTick();
}

export function setupI18n(
  router: Router,
  options = defaultOptions,
) {
  if (options !== defaultOptions) {
    options = { ...defaultOptions, ...options };
  }

  const i18n = createI18n(options);

  // Load default locale synchronously before router setup
  const setupRouter = async () => {
    await loadLocaleMessages(i18n, options.locale);
    setI18nLanguage(i18n, options.locale);

    router.beforeEach(async (to, from, next) => {
      const paramsLocale = to.params.locale as string;
      const pathParts = to.path.split('/');
      const path = pathParts.filter((part) => part).join('/');
      const targetPath = path ? `/${path}` : '';

      if (!SUPPORTED_LOCALES.includes(paramsLocale)) {
        return next(`/${options.locale}${targetPath}`);
      }

      if (!i18n.global.availableLocales.includes(paramsLocale)) {
        await loadLocaleMessages(i18n, paramsLocale);
      }

      setI18nLanguage(i18n, paramsLocale);

      return next();
    });
  };

  setupRouter();

  return i18n;
}
